import AppComponent from '../base/app-component.ts';
import { templateParseToElement } from '../lib/template-parse.ts';
import { impostaSearchableSelect } from '../lib/searchable-select.ts';
import type { DelegaAltroTerritorioBodyInput } from '../lib/api/deleghe-altri-territori.ts';
import { oggi, daLavoratore, bodyNominativo, messaggioErrore, type Nominativo } from '../lib/nominativo-altro-territorio.ts';
import type { OperatoreCombobox } from './operatore-combobox.ts';
import type { DelegaAltroTerritorioArricchita } from '@/types/entities/delega-altro-territorio.ts';
import type { LavoratoreCompleto } from '@/types/entities/ricerca.ts';

const LUNGHEZZA_CF = 16;
const RITARDO_CHIUSURA_MS = 800;

/**
 * wc-edit-delega-altro-territorio — Inserimento e modifica di una delega di altro territorio (punto 7).
 *
 * Attributi: `delega-id` (0 = nuova), `data-options` `{ territori: Territorio[], erroreTerritori?: string }`.
 * Nominativo dall'anagrafica (`wc-ricerca-lavoratori mode="seleziona"`) o inserito a mano
 * (CF verificato con decodificaCf; se è già in anagrafica viene collegato).
 * Successo: notifyAndClose('delega-altro-territorio-saved').
 */
export class EditDelegaAltroTerritorio extends AppComponent {
  static override componentName = 'wc-edit-delega-altro-territorio';

  private delegaId = 0;
  private nominativo: Nominativo | null = null;
  /** CF del blocco manuale verificato dal server */
  private cfValido = false;
  private cfInVerifica = '';

  protected override get templateId() { return 'edit-delega-altro-territorio'; }

  protected override buildRenderingData(): Record<string, unknown> {
    return { ...super.buildRenderingData(), oggi: oggi(), soloPresenti: true };
  }

  protected override afterRender(): void {
    this.delegaId = this.attributeNumber('delega-id');
    this.setupNominativo();
    const form = this.$('[data-form="delega-altro-territorio"]');
    if (form) this.listen(form, 'submit', (e) => { e.preventDefault(); this.salva(); });
    if (this.delegaId) this.caricaDelega();
  }

  // --- Nominativo ---

  private setupNominativo(): void {
    const ricerca = this.$('wc-ricerca-lavoratori');
    if (ricerca) {
      this.listen(ricerca, 'lavoratore-selezionato', (e) =>
        this.mostraDelega(daLavoratore((e as CustomEvent<LavoratoreCompleto>).detail)));
    }
    this.delegate.on('click', '[data-action="inserimento-manuale"]', (e) => {
      e.preventDefault();
      this.$('[data-manuale]')?.classList.remove('d-none');
      this.campo('codiceFiscale')?.focus();
    });
    this.delegate.on('input', '[data-field="codiceFiscale"]', () => this.verificaCf());
    this.delegate.on('click', '[data-action="conferma-manuale"]', () => this.confermaManuale());
    this.delegate.on('click', '[data-action="cambia-nominativo"]', () => this.mostraStep('nominativo'));
  }

  private async verificaCf(): Promise<void> {
    const cf = this.valore('codiceFiscale').toUpperCase();
    this.cfInVerifica = cf;
    this.cfValido = false;
    this.segnalaCf('');
    if (cf.length !== LUNGHEZZA_CF) return;

    const result = await this.api.lavoratori.decodificaCf(cf);
    if (cf !== this.cfInVerifica) return;
    if (!result.ok) { this.segnalaCf(result.error); return; }

    this.cfValido = true;
    this.imposta('dataNascita', result.data.dataNascita);
    if (result.data.lavoratore) this.mostraDelega(daLavoratore(result.data.lavoratore, true));
  }

  private segnalaCf(messaggio: string): void {
    this.campo('codiceFiscale')?.classList.toggle('is-invalid', !!messaggio);
    const errore = this.$('[data-cf-errore]');
    if (errore) errore.textContent = messaggio;
  }

  private confermaManuale(): void {
    if (!this.cfValido) { Weext.toast('Inserire un codice fiscale valido', { type: 'warning' }); return; }
    const n: Nominativo = {
      lavoratoreId: null,
      codiceFiscale: this.valore('codiceFiscale').toUpperCase(),
      cognome: this.valore('cognome').toUpperCase(),
      nome: this.valore('nome').toUpperCase(),
      dataNascita: this.valore('dataNascita') || null,
    };
    if (!n.cognome || !n.nome || !n.dataNascita) {
      Weext.toast('Cognome, nome e data di nascita sono obbligatori', { type: 'warning' });
      return;
    }
    this.mostraDelega(n);
  }

  private mostraDelega(n: Nominativo): void {
    this.nominativo = n;
    const box = this.$('[data-nominativo]');
    if (box) templateParseToElement('edit-delega-altro-territorio-nominativo', box, { ...n });
    this.mostraStep('delega');
  }

  private mostraStep(step: 'nominativo' | 'delega'): void {
    for (const el of this.$$('[data-step]')) el.classList.toggle('d-none', el.dataset.step !== step);
  }

  // --- Modifica: caricamento ---

  private async caricaDelega(): Promise<void> {
    const result = await this.api.delegheAltriTerritori.dettaglio(this.delegaId);
    if (!result.ok) { Weext.toast(result.error, { type: 'danger' }); return; }
    const d = result.data;
    if (!d.lavoratoreId) this.precompilaManuale(d);
    this.mostraDelega({ lavoratoreId: d.lavoratoreId, codiceFiscale: d.codiceFiscale, cognome: d.cognome, nome: d.nome, dataNascita: d.dataNascita });
    this.precompilaDelega(d);
  }

  private precompilaManuale(d: DelegaAltroTerritorioArricchita): void {
    for (const campo of ['codiceFiscale', 'cognome', 'nome', 'dataNascita'] as const) this.imposta(campo, d[campo]);
    this.cfValido = true;
    this.$('[data-manuale]')?.classList.remove('d-none');
  }

  private precompilaDelega(d: DelegaAltroTerritorioArricchita): void {
    const select = this.$('select[data-field="territorioId"]') as HTMLSelectElement | null;
    if (select) templateParseToElement('shared-opzioni-territori', select, { ...this.buildRenderingData(), attuale: d.territorioId });
    impostaSearchableSelect(select, String(d.territorioId));
    this.imposta('dataDelega', d.dataDelega);
    this.imposta('note', d.note ?? '');
    if (d.operatore) this.combo?.preseleziona(Number(d.operatore), d.operatoreNome ?? d.operatore);
    this.$('[data-avviso-statini]')?.classList.toggle('d-none', !d.statiniInviataAt);
  }

  // --- Salvataggio ---

  private raccogliBody(): DelegaAltroTerritorioBodyInput | null {
    if (!this.nominativo) return null;
    const territorioId = Number(this.valore('territorioId'));
    const dataDelega = this.valore('dataDelega');
    const operatore = this.combo?.value;
    if (!territorioId || !dataDelega || !operatore) {
      Weext.toast('Territorio, data delega e operatore sono obbligatori', { type: 'warning' });
      return null;
    }
    if (dataDelega > oggi()) {
      Weext.toast('La data delega non può essere futura', { type: 'warning' });
      return null;
    }
    const note = this.valore('note') || null;
    return { ...bodyNominativo(this.nominativo), territorioId, dataDelega, operatore: String(operatore), note };
  }

  private async salva(): Promise<void> {
    const body = this.raccogliBody();
    if (!body) return;
    const btn = this.$('[data-submit-btn]') as HTMLButtonElement | null;
    if (btn) btn.disabled = true;
    this.mostraErrore('');

    const api = this.api.delegheAltriTerritori;
    const result = this.delegaId ? await api.aggiorna(this.delegaId, body) : await api.crea(body);
    if (!result.ok) {
      this.mostraErrore(messaggioErrore(result));
      if (btn) btn.disabled = false;
      return;
    }

    Weext.toast('Delega salvata', { type: 'success' });
    const detail = { type: 'delega-altro-territorio-salvata', id: result.data.id };
    setTimeout(() => this.notifyAndClose('delega-altro-territorio-saved', detail), RITARDO_CHIUSURA_MS);
  }

  private mostraErrore(messaggio: string): void {
    const container = this.$('[data-errore]');
    if (!container) return;
    if (messaggio) templateParseToElement('shared-error-state', container, { message: messaggio });
    else container.innerHTML = '';
  }

  // --- Helper campi ---

  private get combo(): OperatoreCombobox | null {
    return this.$('wc-operatore-combobox') as OperatoreCombobox | null;
  }

  private campo = (nome: string) => this.$(`[data-field="${nome}"]`) as HTMLInputElement | null;

  private valore = (nome: string): string => this.campo(nome)?.value.trim() ?? '';

  private imposta(nome: string, valore: string): void {
    const el = this.campo(nome);
    if (el) el.value = valore;
  }
}
